import * as SheetJS from 'xlsx';
import { XLSX_MIME_TYPE, sanitizeSpreadsheetCellValue } from './spreadsheetCell';

export type WorkBook = SheetJS.WorkBook;
export type WorkSheet = SheetJS.WorkSheet;
export type CellObject = SheetJS.CellObject;

export { XLSX_MIME_TYPE, sanitizeSpreadsheetCellValue };
export const MAX_XLSX_COMPRESSED_BYTES = 10 * 1024 * 1024;

const MAX_ARCHIVE_ENTRIES = 512;
const MAX_ARCHIVE_UNCOMPRESSED_BYTES = 40 * 1024 * 1024;
const MAX_SINGLE_ENTRY_BYTES = 16 * 1024 * 1024;
const MAX_COMPRESSION_RATIO = 500;
const MAX_SHEETS = 32;
const MAX_ROWS_PER_SHEET = 50_000;
const MAX_COLUMNS_PER_SHEET = 512;
const MAX_CELLS = 250_000;
const MAX_CELL_STRING_LENGTH = 32_767;
const MAX_TOTAL_STRING_LENGTH = 8 * 1024 * 1024;
const MAX_FILE_NAME_LENGTH = 255;
const EOCD_MIN_SIZE = 22;
const EOCD_MAX_SEARCH = 65_535 + EOCD_MIN_SIZE;

const SUSPICIOUS_ENTRY_PATTERNS = [
  /^xl\/externalLinks\//i,
  /^xl\/embeddings\//i,
  /^xl\/activeX\//i,
  /^xl\/charts\//i,
  /^xl\/connections\.xml$/i,
  /^xl\/ctrlProps\//i,
  /^customXml\//i,
  /^xl\/drawings\//i,
  /^xl\/macrosheets\//i,
  /^xl\/dialogsheets\//i,
  /^xl\/media\//i,
  /^xl\/model\//i,
  /^xl\/pivotCache\//i,
  /^xl\/queryTables\//i,
  /^xl\/webExtensions\//i,
  /(?:^|\/)vbaProject\.bin$/i,
  /(?:^|\/)vbaData\.xml$/i,
  /^xl\/calcChain\.xml$/i,
];

const INTERNAL_FORMULA_FUNCTIONS = new Set([
  'AND',
  'IF',
  'MAX',
  'OR',
  'ROUND',
  'SUM',
  'SUMIFS',
]);

const internalFormulaCells = new WeakSet<object>();

export class SecureXlsxError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'SecureXlsxError';
  }
}

export interface XlsxFileMetadata {
  fileName: string;
  mimeType: string;
}

interface ArchiveEntry {
  name: string;
  method: number;
  crc32: number;
  compressedSize: number;
  uncompressedSize: number;
  dataOffset: number;
  localHeaderOffset: number;
  rangeEnd: number;
}

const fail = (message: string, code: string): never => {
  throw new SecureXlsxError(message, code);
};

const asBytes = (input: Uint8Array | ArrayBuffer): Uint8Array =>
  input instanceof Uint8Array
    ? new Uint8Array(input.buffer, input.byteOffset, input.byteLength)
    : new Uint8Array(input);

const viewOf = (bytes: Uint8Array): DataView =>
  new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

const assertBounds = (
  offset: number,
  length: number,
  total: number,
  code = 'XLSX_TRUNCATED',
): void => {
  if (
    !Number.isSafeInteger(offset) ||
    !Number.isSafeInteger(length) ||
    offset < 0 ||
    length < 0 ||
    offset + length > total
  ) {
    fail('The XLSX archive is truncated or malformed.', code);
  }
};

const hasZip64Extra = (extra: Uint8Array): boolean => {
  const view = viewOf(extra);
  let cursor = 0;
  while (cursor + 4 <= extra.byteLength) {
    const id = view.getUint16(cursor, true);
    const size = view.getUint16(cursor + 2, true);
    cursor += 4;
    assertBounds(cursor, size, extra.byteLength, 'XLSX_INVALID_EXTRA_FIELD');
    if (id === 0x0001) return true;
    cursor += size;
  }
  if (cursor !== extra.byteLength) {
    fail('The XLSX archive contains a malformed ZIP extra field.', 'XLSX_INVALID_EXTRA_FIELD');
  }
  return false;
};

const decodeEntryName = (bytes: Uint8Array): string => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return fail('The XLSX archive contains an invalid entry name.', 'XLSX_INVALID_ENTRY_NAME');
  }
};

const assertSafeEntryName = (name: string): void => {
  const normalized = name.replace(/\\/g, '/');
  const segments = normalized.split('/');
  if (
    !name ||
    name.includes('\0') ||
    normalized.startsWith('/') ||
    /^[a-z]:/i.test(normalized) ||
    segments.some((segment) => segment === '.' || segment === '..')
  ) {
    fail('The XLSX archive contains an unsafe entry path.', 'XLSX_UNSAFE_ENTRY_PATH');
  }
  if (SUSPICIOUS_ENTRY_PATTERNS.some((pattern) => pattern.test(normalized))) {
    fail(
      'Macros, external links, ActiveX controls, and embedded objects are not allowed.',
      'XLSX_ACTIVE_CONTENT',
    );
  }
};

const findEndOfCentralDirectory = (bytes: Uint8Array): number => {
  const view = viewOf(bytes);
  const lowerBound = Math.max(0, bytes.byteLength - EOCD_MAX_SEARCH);
  for (let offset = bytes.byteLength - EOCD_MIN_SIZE; offset >= lowerBound; offset -= 1) {
    if (view.getUint32(offset, true) !== 0x06054b50) continue;
    const commentLength = view.getUint16(offset + 20, true);
    if (offset + EOCD_MIN_SIZE + commentLength === bytes.byteLength) return offset;
  }
  return fail('The file is not a complete XLSX ZIP archive.', 'XLSX_EOCD_MISSING');
};

const parseArchiveEntries = (bytes: Uint8Array): ArchiveEntry[] => {
  if (bytes.byteLength === 0 || bytes.byteLength > MAX_XLSX_COMPRESSED_BYTES) {
    fail(
      `XLSX files must be between 1 byte and ${MAX_XLSX_COMPRESSED_BYTES} bytes.`,
      'XLSX_COMPRESSED_SIZE_LIMIT',
    );
  }
  if (
    bytes.byteLength < EOCD_MIN_SIZE ||
    bytes[0] !== 0x50 ||
    bytes[1] !== 0x4b ||
    bytes[2] !== 0x03 ||
    bytes[3] !== 0x04
  ) {
    fail('The file does not have XLSX ZIP magic bytes.', 'XLSX_INVALID_MAGIC');
  }

  const view = viewOf(bytes);
  const eocdOffset = findEndOfCentralDirectory(bytes);
  const diskNumber = view.getUint16(eocdOffset + 4, true);
  const centralDirectoryDisk = view.getUint16(eocdOffset + 6, true);
  const entriesOnDisk = view.getUint16(eocdOffset + 8, true);
  const entryCount = view.getUint16(eocdOffset + 10, true);
  const centralDirectorySize = view.getUint32(eocdOffset + 12, true);
  const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);

  if (
    diskNumber !== 0 ||
    centralDirectoryDisk !== 0 ||
    entriesOnDisk !== entryCount
  ) {
    fail('Multi-disk ZIP archives are not accepted as XLSX files.', 'XLSX_MULTI_DISK');
  }
  if (
    entryCount === 0xffff ||
    centralDirectorySize === 0xffffffff ||
    centralDirectoryOffset === 0xffffffff ||
    (eocdOffset >= 20 && view.getUint32(eocdOffset - 20, true) === 0x07064b50)
  ) {
    fail('ZIP64 XLSX archives are not accepted.', 'XLSX_ZIP64');
  }
  if (entryCount === 0 || entryCount > MAX_ARCHIVE_ENTRIES) {
    fail(
      `XLSX archives may contain at most ${MAX_ARCHIVE_ENTRIES} entries.`,
      'XLSX_ENTRY_LIMIT',
    );
  }
  assertBounds(
    centralDirectoryOffset,
    centralDirectorySize,
    bytes.byteLength,
    'XLSX_INVALID_CENTRAL_DIRECTORY',
  );
  if (centralDirectoryOffset + centralDirectorySize !== eocdOffset) {
    fail('The XLSX central directory has an invalid boundary.', 'XLSX_INVALID_CENTRAL_DIRECTORY');
  }

  const entries: ArchiveEntry[] = [];
  const names = new Set<string>();
  const localOffsets = new Set<number>();
  let cursor = centralDirectoryOffset;
  let totalUncompressed = 0;

  for (let index = 0; index < entryCount; index += 1) {
    assertBounds(cursor, 46, bytes.byteLength);
    if (view.getUint32(cursor, true) !== 0x02014b50) {
      fail('The XLSX central directory is malformed.', 'XLSX_INVALID_CENTRAL_DIRECTORY');
    }

    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const crc32 = view.getUint32(cursor + 16, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const uncompressedSize = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const startDisk = view.getUint16(cursor + 34, true);
    const localHeaderOffset = view.getUint32(cursor + 42, true);
    const recordLength = 46 + nameLength + extraLength + commentLength;
    assertBounds(cursor, recordLength, bytes.byteLength);

    if ((flags & 0x0001) !== 0 || (flags & 0x0040) !== 0) {
      fail('Encrypted XLSX archives are not accepted.', 'XLSX_ENCRYPTED');
    }
    if ((flags & ~0x080e) !== 0) {
      fail('The XLSX archive uses unsupported ZIP flags.', 'XLSX_UNSUPPORTED_ZIP_FLAGS');
    }
    if (method !== 0 && method !== 8) {
      fail('The XLSX archive uses an unsupported compression method.', 'XLSX_UNSUPPORTED_COMPRESSION');
    }
    if (
      startDisk !== 0 ||
      compressedSize === 0xffffffff ||
      uncompressedSize === 0xffffffff ||
      localHeaderOffset === 0xffffffff
    ) {
      fail('ZIP64 XLSX archives are not accepted.', 'XLSX_ZIP64');
    }

    const nameBytes = bytes.subarray(cursor + 46, cursor + 46 + nameLength);
    const extra = bytes.subarray(
      cursor + 46 + nameLength,
      cursor + 46 + nameLength + extraLength,
    );
    if (hasZip64Extra(extra)) {
      fail('ZIP64 XLSX archives are not accepted.', 'XLSX_ZIP64');
    }
    const name = decodeEntryName(nameBytes);
    assertSafeEntryName(name);
    const dedupeName = name.replace(/\\/g, '/').toLowerCase();
    if (names.has(dedupeName)) {
      fail('The XLSX archive contains duplicate entry names.', 'XLSX_DUPLICATE_ENTRY');
    }
    names.add(dedupeName);

    if (uncompressedSize > MAX_SINGLE_ENTRY_BYTES) {
      fail(
        `An XLSX archive entry exceeds ${MAX_SINGLE_ENTRY_BYTES} uncompressed bytes.`,
        'XLSX_SINGLE_ENTRY_LIMIT',
      );
    }
    totalUncompressed += uncompressedSize;
    if (totalUncompressed > MAX_ARCHIVE_UNCOMPRESSED_BYTES) {
      fail(
        `XLSX archives may expand to at most ${MAX_ARCHIVE_UNCOMPRESSED_BYTES} bytes.`,
        'XLSX_UNCOMPRESSED_SIZE_LIMIT',
      );
    }
    if (
      uncompressedSize > 0 &&
      (compressedSize === 0 || uncompressedSize / compressedSize > MAX_COMPRESSION_RATIO)
    ) {
      fail('The XLSX archive has an unsafe compression ratio.', 'XLSX_COMPRESSION_RATIO');
    }

    assertBounds(localHeaderOffset, 30, bytes.byteLength);
    if (localOffsets.has(localHeaderOffset)) {
      fail('The XLSX archive reuses a local entry offset.', 'XLSX_OVERLAPPING_ENTRIES');
    }
    localOffsets.add(localHeaderOffset);
    if (view.getUint32(localHeaderOffset, true) !== 0x04034b50) {
      fail('The XLSX archive has an invalid local entry header.', 'XLSX_INVALID_LOCAL_HEADER');
    }
    const localFlags = view.getUint16(localHeaderOffset + 6, true);
    const localMethod = view.getUint16(localHeaderOffset + 8, true);
    const localCrc32 = view.getUint32(localHeaderOffset + 14, true);
    const localCompressedSize = view.getUint32(localHeaderOffset + 18, true);
    const localUncompressedSize = view.getUint32(localHeaderOffset + 22, true);
    const localNameLength = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
    if (
      (localFlags & 0x0001) !== 0 ||
      (localFlags & 0x0040) !== 0 ||
      localFlags !== flags ||
      localMethod !== method
    ) {
      fail('The XLSX local entry header conflicts with its directory.', 'XLSX_INVALID_LOCAL_HEADER');
    }
    assertBounds(
      localHeaderOffset + 30,
      localNameLength + localExtraLength,
      bytes.byteLength,
    );
    const localExtra = bytes.subarray(
      localHeaderOffset + 30 + localNameLength,
      localHeaderOffset + 30 + localNameLength + localExtraLength,
    );
    if (hasZip64Extra(localExtra)) {
      fail('ZIP64 XLSX archives are not accepted.', 'XLSX_ZIP64');
    }
    const localName = decodeEntryName(
      bytes.subarray(localHeaderOffset + 30, localHeaderOffset + 30 + localNameLength),
    );
    if (localName !== name) {
      fail('The XLSX local entry name conflicts with its directory.', 'XLSX_INVALID_LOCAL_HEADER');
    }
    const dataOffset = localHeaderOffset + 30 + localNameLength + localExtraLength;
    assertBounds(dataOffset, compressedSize, bytes.byteLength);
    let rangeEnd = dataOffset + compressedSize;
    if ((flags & 0x0008) === 0) {
      if (
        localCrc32 !== crc32 ||
        localCompressedSize !== compressedSize ||
        localUncompressedSize !== uncompressedSize
      ) {
        fail('The XLSX local entry sizes conflict with its directory.', 'XLSX_INVALID_LOCAL_HEADER');
      }
    } else {
      assertBounds(rangeEnd, 12, bytes.byteLength);
      const hasSignature = view.getUint32(rangeEnd, true) === 0x08074b50;
      const descriptorOffset = rangeEnd + (hasSignature ? 4 : 0);
      assertBounds(descriptorOffset, 12, bytes.byteLength);
      if (
        view.getUint32(descriptorOffset, true) !== crc32 ||
        view.getUint32(descriptorOffset + 4, true) !== compressedSize ||
        view.getUint32(descriptorOffset + 8, true) !== uncompressedSize
      ) {
        fail('The XLSX data descriptor conflicts with its directory.', 'XLSX_INVALID_DATA_DESCRIPTOR');
      }
      rangeEnd = descriptorOffset + 12;
    }
    if (rangeEnd > centralDirectoryOffset) {
      fail('The XLSX archive contains overlapping entries.', 'XLSX_OVERLAPPING_ENTRIES');
    }

    entries.push({
      name,
      method,
      crc32,
      compressedSize,
      uncompressedSize,
      dataOffset,
      localHeaderOffset,
      rangeEnd,
    });
    cursor += recordLength;
  }

  if (cursor !== centralDirectoryOffset + centralDirectorySize) {
    fail('The XLSX central directory entry count is inconsistent.', 'XLSX_INVALID_CENTRAL_DIRECTORY');
  }
  const ranges = [...entries].sort(
    (left, right) => left.localHeaderOffset - right.localHeaderOffset,
  );
  if (ranges[0]?.localHeaderOffset !== 0) {
    fail('The XLSX archive contains data outside its entries.', 'XLSX_HIDDEN_ARCHIVE_DATA');
  }
  for (let index = 1; index < ranges.length; index += 1) {
    if (ranges[index].localHeaderOffset !== ranges[index - 1].rangeEnd) {
      fail('The XLSX archive contains overlapping or hidden data.', 'XLSX_HIDDEN_ARCHIVE_DATA');
    }
  }
  if (ranges[ranges.length - 1]?.rangeEnd !== centralDirectoryOffset) {
    fail('The XLSX archive contains data outside its entries.', 'XLSX_HIDDEN_ARCHIVE_DATA');
  }
  return entries;
};

let crcTable: Uint32Array | undefined;

const getCrcTable = (): Uint32Array => {
  if (crcTable) return crcTable;
  crcTable = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) !== 0 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    crcTable[index] = value >>> 0;
  }
  return crcTable;
};

const crc32Of = (bytes: Uint8Array): number => {
  const table = getCrcTable();
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const extractEntry = async (
  archive: Uint8Array,
  entry: ArchiveEntry,
): Promise<Uint8Array> => {
  const compressed = archive.slice(
    entry.dataOffset,
    entry.dataOffset + entry.compressedSize,
  );
  let uncompressed: Uint8Array;
  try {
    if (entry.method === 0) {
      uncompressed = compressed;
    } else {
      if (typeof DecompressionStream !== 'function') {
        return fail('This browser cannot safely inspect compressed XLSX files.', 'XLSX_DECOMPRESSION_UNAVAILABLE');
      }
      const stream = new Blob([compressed.buffer]).stream().pipeThrough(
        new DecompressionStream('deflate-raw' as CompressionFormat),
      );
      const reader = stream.getReader();
      uncompressed = new Uint8Array(entry.uncompressedSize);
      let outputOffset = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (outputOffset + value.byteLength > entry.uncompressedSize) {
          await reader.cancel();
          return fail(
            'The XLSX archive expanded beyond its declared size.',
            'XLSX_DECOMPRESSED_SIZE_LIMIT',
          );
        }
        uncompressed.set(value, outputOffset);
        outputOffset += value.byteLength;
      }
      if (outputOffset !== entry.uncompressedSize) {
        return fail(
          'The XLSX archive did not match its declared size.',
          'XLSX_CHECKSUM_FAILED',
        );
      }
    }
  } catch (error) {
    if (error instanceof SecureXlsxError) throw error;
    return fail('The XLSX archive contains invalid compressed data.', 'XLSX_DECOMPRESSION_FAILED');
  }
  if (
    uncompressed.byteLength !== entry.uncompressedSize ||
    crc32Of(uncompressed) !== entry.crc32
  ) {
    fail('The XLSX archive failed its size or checksum validation.', 'XLSX_CHECKSUM_FAILED');
  }
  return uncompressed;
};

const validateArchive = async (input: Uint8Array | ArrayBuffer): Promise<Uint8Array> => {
  const bytes = asBytes(input);
  const entries = parseArchiveEntries(bytes);
  const decoder = new TextDecoder('utf-8', { fatal: false });
  const normalizedNames = new Set(
    entries.map((entry) => entry.name.replace(/\\/g, '/')),
  );
  for (const requiredName of [
    '[Content_Types].xml',
    '_rels/.rels',
    'xl/workbook.xml',
    'xl/_rels/workbook.xml.rels',
  ]) {
    if (!normalizedNames.has(requiredName)) {
      fail('The archive is missing a required XLSX part.', 'XLSX_REQUIRED_PART_MISSING');
    }
  }

  let worksheetCount = 0;
  let worksheetCellCount = 0;

  for (const entry of entries) {
    if (entry.name.endsWith('/')) {
      if (entry.compressedSize !== 0 || entry.uncompressedSize !== 0 || entry.crc32 !== 0) {
        fail('XLSX directory entries must be empty.', 'XLSX_INVALID_DIRECTORY_ENTRY');
      }
      continue;
    }
    const content = await extractEntry(bytes, entry);
    const normalized = entry.name.replace(/\\/g, '/');
    const isXml = normalized.endsWith('.xml') || normalized.endsWith('.rels');
    if (isXml) {
      const xml = decoder.decode(content);
      if (/<!DOCTYPE|<!ENTITY/i.test(xml)) {
        fail('DTD and entity declarations are not accepted in XLSX XML.', 'XLSX_UNSAFE_XML');
      }
    }
    const inspect =
      normalized === '[Content_Types].xml' ||
      normalized === 'xl/workbook.xml' ||
      normalized.endsWith('.rels') ||
      /^xl\/worksheets\/[^/]+\.xml$/i.test(normalized);
    if (!inspect) continue;
    const xml = decoder.decode(content);
    if (
      normalized === '[Content_Types].xml' &&
      !/application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet\.main\+xml/i.test(xml)
    ) {
      fail('The archive is not an XLSX workbook.', 'XLSX_CONTENT_TYPE_INVALID');
    }
    if (
      /TargetMode\s*=\s*["']External["']/i.test(xml) ||
      /<(?:[A-Za-z_][\w.-]*:)?externalReferences(?=[\s/>])/i.test(xml) ||
      /application\/vnd\.ms-office\.(?:vbaProject|activeX|oleObject)/i.test(xml) ||
      /application\/vnd\.ms-excel\.(?:sheet\.macroEnabled|addin\.macroEnabled)/i.test(xml)
    ) {
      fail(
        'Macros, external relationships, ActiveX controls, and embedded objects are not allowed.',
        'XLSX_ACTIVE_CONTENT',
      );
    }
    if (/^xl\/worksheets\//i.test(normalized)) {
      worksheetCount += 1;
      if (worksheetCount > MAX_SHEETS) {
        fail(`Imported workbooks may contain at most ${MAX_SHEETS} worksheets.`, 'XLSX_SHEET_LIMIT');
      }
      if (/<(?:[A-Za-z_][\w.-]*:)?f(?=[\s/>])/i.test(xml)) {
        fail('Spreadsheet formulas are not accepted in imported XLSX files.', 'XLSX_FORMULA');
      }
      const rows = xml.match(/<(?:[A-Za-z_][\w.-]*:)?row(?=[\s/>])/gi)?.length ?? 0;
      if (rows > MAX_ROWS_PER_SHEET) {
        fail(`Worksheets may contain at most ${MAX_ROWS_PER_SHEET} rows.`, 'XLSX_DIMENSION_LIMIT');
      }
      for (const match of xml.matchAll(/<(?:[A-Za-z_][\w.-]*:)?c(?=[\s/>])/gi)) {
        void match;
        worksheetCellCount += 1;
        if (worksheetCellCount > MAX_CELLS) {
          fail(`Imported workbooks may contain at most ${MAX_CELLS} cells.`, 'XLSX_CELL_LIMIT');
        }
      }
    }
  }
  if (worksheetCount === 0) {
    fail('The archive contains no XLSX worksheet.', 'XLSX_REQUIRED_PART_MISSING');
  }
  return bytes;
};

const hasUnsafeFileNameCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (character === '/' || character === '\\' || codePoint <= 0x1f || codePoint === 0x7f) {
      return true;
    }
  }
  return false;
};

export const assertXlsxFileMetadata = ({ fileName, mimeType }: XlsxFileMetadata): void => {
  const normalizedFileName = String(fileName ?? '');
  if (
    normalizedFileName !== normalizedFileName.trim() ||
    normalizedFileName.length < 6 ||
    normalizedFileName.length > MAX_FILE_NAME_LENGTH ||
    hasUnsafeFileNameCharacter(normalizedFileName) ||
    !/\.xlsx$/i.test(normalizedFileName)
  ) {
    fail('Only .xlsx files are accepted.', 'XLSX_EXTENSION_REQUIRED');
  }
  if (String(mimeType ?? '').trim().toLowerCase() !== XLSX_MIME_TYPE) {
    fail('The selected file must use the XLSX media type.', 'XLSX_MIME_REQUIRED');
  }
};

const assertWorkbookLimits = (workbook: SheetJS.WorkBook): void => {
  if (workbook.SheetNames.length === 0 || workbook.SheetNames.length > MAX_SHEETS) {
    fail(`Imported workbooks may contain at most ${MAX_SHEETS} worksheets.`, 'XLSX_SHEET_LIMIT');
  }
  if (new Set(workbook.SheetNames).size !== workbook.SheetNames.length) {
    fail('Imported workbook sheet names must be unique.', 'XLSX_DUPLICATE_SHEET');
  }
  if ((workbook.Workbook?.Names?.length ?? 0) > 0) {
    fail('Defined-name formulas are not accepted in imported XLSX files.', 'XLSX_FORMULA');
  }

  let totalCells = 0;
  let totalStringLength = 0;
  for (const sheetName of workbook.SheetNames) {
    const worksheet = workbook.Sheets[sheetName];
    if (!worksheet) {
      fail('The workbook references a missing worksheet.', 'XLSX_INVALID_WORKBOOK');
    }
    const refs = [
      worksheet['!ref'],
      (worksheet as SheetJS.WorkSheet & { '!fullref'?: string })['!fullref'],
    ].filter((value): value is string => typeof value === 'string');
    for (const ref of refs) {
      let range: SheetJS.Range;
      try {
        range = SheetJS.utils.decode_range(ref);
      } catch {
        return fail('A worksheet has an invalid cell range.', 'XLSX_INVALID_RANGE');
      }
      if (
        range.s.r < 0 ||
        range.s.c < 0 ||
        range.e.r + 1 > MAX_ROWS_PER_SHEET ||
        range.e.c + 1 > MAX_COLUMNS_PER_SHEET
      ) {
        fail(
          `Worksheets may contain at most ${MAX_ROWS_PER_SHEET} rows and ${MAX_COLUMNS_PER_SHEET} columns.`,
          'XLSX_DIMENSION_LIMIT',
        );
      }
    }
    for (const [address, rawCell] of Object.entries(worksheet)) {
      if (address.startsWith('!')) continue;
      const cell = rawCell as SheetJS.CellObject;
      totalCells += 1;
      if (totalCells > MAX_CELLS) {
        fail(`Imported workbooks may contain at most ${MAX_CELLS} cells.`, 'XLSX_CELL_LIMIT');
      }
      if (cell.f != null) {
        fail('Spreadsheet formulas are not accepted in imported XLSX files.', 'XLSX_FORMULA');
      }
      if (cell.t === 'e') {
        fail('Spreadsheet error cells are not accepted in imported XLSX files.', 'XLSX_ERROR_CELL');
      }
      if (cell.l?.Target) {
        fail('Spreadsheet hyperlinks are not accepted in imported XLSX files.', 'XLSX_EXTERNAL_LINK');
      }
      if (typeof cell.v === 'string') {
        if (cell.v.length > MAX_CELL_STRING_LENGTH) {
          fail(
            `Cell strings may contain at most ${MAX_CELL_STRING_LENGTH} characters.`,
            'XLSX_STRING_LIMIT',
          );
        }
        totalStringLength += cell.v.length;
        if (totalStringLength > MAX_TOTAL_STRING_LENGTH) {
          fail('The workbook contains too much text.', 'XLSX_TOTAL_STRING_LIMIT');
        }
      }
    }
  }
};

export const readSecureXlsx = async (
  input: Uint8Array | ArrayBuffer,
  metadata: XlsxFileMetadata,
): Promise<SheetJS.WorkBook> => {
  assertXlsxFileMetadata(metadata);
  const bytes = await validateArchive(input);
  let workbook: SheetJS.WorkBook;
  try {
    workbook = SheetJS.read(bytes, {
      type: 'array',
      cellDates: true,
      cellFormula: true,
      sheetRows: MAX_ROWS_PER_SHEET + 1,
    });
  } catch {
    return fail('The XLSX workbook could not be parsed safely.', 'XLSX_PARSE_FAILED');
  }
  assertWorkbookLimits(workbook);
  return workbook;
};

export const readSecureXlsxFile = async (file: File): Promise<SheetJS.WorkBook> =>
  readSecureXlsx(await file.arrayBuffer(), {
    fileName: file.name,
    mimeType: file.type,
  });

export const escapeSpreadsheetCsvCell = (
  value: unknown,
  delimiter = ',',
): string => {
  if (
    delimiter.length !== 1 ||
    delimiter === '"' ||
    delimiter === '\r' ||
    delimiter === '\n'
  ) {
    fail('CSV exports require a single safe delimiter.', 'XLSX_UNSAFE_CSV_DELIMITER');
  }
  const sanitized = sanitizeSpreadsheetCellValue(value == null ? '' : String(value));
  return sanitized.includes(delimiter) ||
    sanitized.includes('"') ||
    sanitized.includes('\r') ||
    sanitized.includes('\n')
    ? `"${sanitized.replace(/"/g, '""')}"`
    : sanitized;
};

const sanitizeRows = (rows: readonly (readonly unknown[])[]): unknown[][] =>
  rows.map((row) =>
    row.map((value) =>
      typeof value === 'string' ? sanitizeSpreadsheetCellValue(value) : value,
    ),
  );

const sanitizeRecords = <T>(rows: T[]): T[] =>
  rows.map((row) =>
    Object.fromEntries(
      Object.entries(row as Record<string, unknown>).map(([key, value]) => [
        key,
        typeof value === 'string' ? sanitizeSpreadsheetCellValue(value) : value,
      ]),
    ) as T,
  );

const assertInternalFormula = (formula: string): void => {
  if (
    !formula ||
    formula.length > 2_048 ||
    !/^[A-Za-z0-9_$():,."<>=+\-*/\s]+$/.test(formula) ||
    /[\[\]!';{}\\]/.test(formula)
  ) {
    fail('An export contains an unsafe internal formula.', 'XLSX_UNSAFE_EXPORT_FORMULA');
  }
  for (const match of formula.matchAll(/([A-Za-z][A-Za-z0-9_.]*)\s*\(/g)) {
    const functionName = match[1];
    if (!functionName || !INTERNAL_FORMULA_FUNCTIONS.has(functionName.toUpperCase())) {
      fail('An export contains a non-allowlisted formula function.', 'XLSX_UNSAFE_EXPORT_FORMULA');
    }
  }
};

export const markInternalFormulaCell = <T extends SheetJS.CellObject>(cell: T): T => {
  const formula =
    typeof cell.f === 'string'
      ? cell.f
      : fail('Only formula cells can be marked as internal.', 'XLSX_UNSAFE_EXPORT_FORMULA');
  assertInternalFormula(formula);
  internalFormulaCells.add(cell);
  return cell;
};

const sanitizeWorksheetForExport = (worksheet: SheetJS.WorkSheet): SheetJS.WorkSheet => {
  for (const [address, rawCell] of Object.entries(worksheet)) {
    if (address.startsWith('!')) continue;
    const cell = rawCell as SheetJS.CellObject;
    if (cell.f != null) {
      if (!internalFormulaCells.has(cell)) {
        fail('Unmarked formulas are not allowed in spreadsheet exports.', 'XLSX_UNSAFE_EXPORT_FORMULA');
      }
      assertInternalFormula(cell.f);
    }
    if (cell.l?.Target) {
      fail('Hyperlinks are not allowed in spreadsheet exports.', 'XLSX_UNSAFE_EXPORT_LINK');
    }
    if (cell.t === 'e') {
      fail('Error cells are not allowed in spreadsheet exports.', 'XLSX_UNSAFE_EXPORT_CELL');
    }
    if (typeof cell.v === 'string') {
      cell.v = sanitizeSpreadsheetCellValue(cell.v);
    }
  }
  return worksheet;
};

export const sanitizeWorkbookForExport = (workbook: SheetJS.WorkBook): SheetJS.WorkBook => {
  if ((workbook.Workbook?.Names?.length ?? 0) > 0 || workbook.vbaraw != null) {
    fail('Active workbook content is not allowed in spreadsheet exports.', 'XLSX_UNSAFE_EXPORT_WORKBOOK');
  }
  for (const sheetName of workbook.SheetNames) {
    const worksheet = workbook.Sheets[sheetName];
    if (worksheet) sanitizeWorksheetForExport(worksheet);
  }
  return workbook;
};

const aoaToSheet = <T>(
  data: T[][],
  options?: SheetJS.AOA2SheetOpts,
): SheetJS.WorkSheet => SheetJS.utils.aoa_to_sheet(sanitizeRows(data), options);

const jsonToSheet = <T>(
  data: T[],
  options?: SheetJS.JSON2SheetOpts,
): SheetJS.WorkSheet =>
  SheetJS.utils.json_to_sheet(sanitizeRecords(data), options);

const sheetAddAoa = <T>(
  worksheet: SheetJS.WorkSheet,
  data: T[][],
  options?: SheetJS.SheetAOAOpts,
): SheetJS.WorkSheet =>
  SheetJS.utils.sheet_add_aoa(worksheet, sanitizeRows(data), options);

const sheetAddJson = <T>(
  worksheet: SheetJS.WorkSheet,
  data: T[],
  options?: SheetJS.JSON2SheetOpts,
): SheetJS.WorkSheet =>
  SheetJS.utils.sheet_add_json(worksheet, sanitizeRecords(data), options);

const sheetToCsv: typeof SheetJS.utils.sheet_to_csv = (worksheet, options) =>
  SheetJS.utils.sheet_to_csv(sanitizeWorksheetForExport(worksheet), options);

const sheetToTxt: typeof SheetJS.utils.sheet_to_txt = (worksheet, options) =>
  SheetJS.utils.sheet_to_txt(sanitizeWorksheetForExport(worksheet), options);

export const utils = {
  ...SheetJS.utils,
  aoa_to_sheet: aoaToSheet,
  json_to_sheet: jsonToSheet,
  sheet_add_aoa: sheetAddAoa,
  sheet_add_json: sheetAddJson,
  sheet_to_csv: sheetToCsv,
  sheet_to_txt: sheetToTxt,
};

export const SSF = SheetJS.SSF;

export const write: typeof SheetJS.write = (workbook, options) =>
  SheetJS.write(sanitizeWorkbookForExport(workbook), options);

export const writeFile: typeof SheetJS.writeFile = (workbook, fileName, options) =>
  SheetJS.writeFile(sanitizeWorkbookForExport(workbook), fileName, options);
